/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Icon,
  Image,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiFileText, FiUploadCloud } from "react-icons/fi";

const GPTDropzone = ({
  files = [], // 👈 controlled files from parent
  onChange, // 👈 callback to update parent files
  accept,
  multiple = true,
  maxFiles,
  maxSize,
  disabled = false,
  showPreviews = true,
  previewHeight = 80,
  previewWidth = 80,
  uploadLabel = "Drop files here or click to browse",
  hintText,
  className,
  renderFile,
  layout = "side",
}) => {
  const [rejections, setRejections] = useState([]);
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const fileBg = useColorModeValue("gray.100", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const dropBg = useColorModeValue("white", "gray.800");
  const dropHoverBg = useColorModeValue("gray.50", "gray.750");

  // 🧩 Handle drop
  const onDropInternal = useCallback(
    (acceptedFiles, fileRejections) => {
      const newFiles = acceptedFiles.map((f) => ({
        file: f,
        preview: f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : undefined,
        id: Math.random().toString(36).slice(2, 9),
      }));

      let merged = multiple ? [...files, ...newFiles] : [...newFiles];
      if (typeof maxFiles === "number") merged = merged.slice(0, maxFiles);

      if (onChange) onChange(merged);
      setRejections(fileRejections);
    },
    [files, onChange, multiple, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
    useDropzone({
      onDrop: onDropInternal,
      accept,
      multiple,
      maxFiles,
      maxSize,
      disabled,
    });

  const removeFile = (index) => {
    const next = [...files];
    next.splice(index, 1);
    onChange && onChange(next);
  };

  const fileRows = useMemo(
    () =>
      files.map((pf, idx) => {
        const onRemove = () => removeFile(idx);
        const isImage = pf.preview || pf.file?.type?.startsWith("image/");

        return (
          <HStack
            key={pf.id || idx}
            spacing={3}
            align="center"
            bg="gray.50"
            w="full"
            p={2}
            borderRadius="md"
            _hover={{ bg: hoverBg }}
          >
            <Flex
              align="center"
              justify="center"
              w={`${previewWidth}px`}
              h={`${previewHeight}px`}
              borderRadius="md"
              overflow="hidden"
              bg={fileBg}
              flexShrink={0}
            >
              {isImage ? (
                <Image
                  src={pf.preview}
                  alt={pf.file.name}
                  objectFit="cover"
                  width="100%"
                  height="100%"
                />
              ) : (
                <Icon as={FiFileText} boxSize={6} />
              )}
            </Flex>

            <VStack spacing={0} align="start" flex="1">
              <Text fontSize="sm" fontWeight="medium" isTruncated>
                {pf.file.name}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {Math.round(pf.file.size / 1024)} KB •{" "}
                {pf.file.type || "unknown"}
              </Text>
            </VStack>

            <CloseButton
              aria-label={`Remove ${pf.file.name}`}
              onClick={onRemove}
            />
          </HStack>
        );
      }),
    [files, hoverBg, fileBg, previewHeight, previewWidth]
  );

  const PreviewList = (
    <Box
      flex="1"
      overflowY="auto"
      maxH="300px"
      pl={layout === "side" ? 4 : 0}
      mt={layout === "bottom" ? 3 : 0}
    >
      {rejections.map((r, i) => (
        <Alert status="error" key={i} borderRadius="md" mb={2}>
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">{r.file.name}</Text>
            <Text fontSize="sm">
              {r.errors.map((e) => e.message).join("; ")}
            </Text>
          </Box>
        </Alert>
      ))}

      {showPreviews && files.length > 0 && (
        <VStack spacing={2} align="stretch">
          <Text fontWeight="500">Preview:</Text>
          {fileRows}
        </VStack>
      )}
    </Box>
  );

  return (
    <Flex
      direction={layout === "side" ? "row" : "column"}
      align="stretch"
      className={className}
      w="full"
    >
      {/* Upload Box */}
      <Box
        {...getRootProps()}
        flex={layout === "side" ? "1" : "none"}
        border="2px dashed"
        borderColor={
          isDragActive ? "blue.400" : isDragReject ? "red.400" : borderColor
        }
        borderRadius="md"
        p={6}
        textAlign="center"
        cursor={disabled ? "not-allowed" : "pointer"}
        bg={dropBg}
        _hover={{ bg: dropHoverBg }}
      >
        <input {...getInputProps()} />
        <VStack spacing={3}>
          <Icon as={FiUploadCloud} boxSize={10} opacity={0.9} />
          <Text fontSize="md" fontWeight="semibold">
            {uploadLabel}
          </Text>
          {hintText && (
            <Text fontSize="sm" color="gray.500">
              {hintText}
            </Text>
          )}
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              isDisabled={disabled}
            >
              Browse
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onChange && onChange([]); // parent clears it
                setRejections([]);
              }}
            >
              Clear
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* Preview Section */}
      {layout === "side" && <Box w="450px">{PreviewList}</Box>}
      {layout === "bottom" && PreviewList}
    </Flex>
  );
};

export default GPTDropzone;
